import { debounce } from 'lodash'
import { SimpleCache } from '@/utils/cache.utils'
import mixpanel from 'mixpanel-browser'

const cache = new SimpleCache()
const maxPresentationCacheTimeInSeconds = 6
const warmCachePresentationDetails = debounce((dispatch, { presentationId, payloadDataPresentation }) => {
  dispatch('getPresentationDetailsWithCacheAction', {
    presentationId,
    payloadDataPresentation,
    maxAgeInSeconds: maxPresentationCacheTimeInSeconds,
    isPreloading: true,
  })
}, 200)

const warmCachePresentationAttributes = debounce((dispatch, { presentationId }) => {
  dispatch('getPresentationAttributesWithCacheAction', {
    presentationId,
    maxAgeInSeconds: maxPresentationCacheTimeInSeconds,
    isPreloading: true,
  })
}, 200)

const actions = {
  getPresentationAttributesWithCacheAction: async ({ dispatch },
    { presentationId, maxAgeInSeconds, isPreloading }) => {
    const action = 'presentationAttributeModule/fetchPresentationAttributes'
    return cache.invoke(() => {
      if (isPreloading) mixpanel.track('Preload > Presentation attributes', { presentationId })
      return dispatch(
        action,
        presentationId,
        { root: true },
      )
    }, {
      key: `${action}-${presentationId}`,
      maxAgeInSeconds,
    })
  },
  getPresentationDetailsWithCacheAction: async ({ dispatch },
    {
      presentationId, payloadDataPresentation, maxAgeInSeconds, isPreloading,
    }) => {
    const action = 'fetchPresentationDetailsFromAPIAction'
    const res = await cache.invoke(() => {
      if (isPreloading) mixpanel.track('Preload > Presentation details', { presentationId })
      return dispatch(
        action,
        { presentationId, payloadDataPresentation },
        { root: true },
      )
    }, {
      key: `${action}-${presentationId}`,
      maxAgeInSeconds,
    })
    // Hand every caller an isolated copy: the editor commits res.body into the
    // store and its destroyed() teardown clears state.presentation.id = null.
    // Because SimpleCache returns the SAME response object on a <6s hit, without
    // this copy that teardown write corrupts the object still held in the cache,
    // so a same-id re-open within the window returns a presentation with id=null
    // and the editor header gate (v-if="presentation.id && …") fails (LONG-699).
    // A NEW wrapper with a fresh shallow body — never a mutation of the cached
    // res — keeps the cached object pristine. Shallow is enough: the teardown
    // only nulls the top-level .id.
    return res && res.body ? { ...res, body: { ...res.body } } : res
  },

  // Drop the cached presentation-details entry so the next fetch — by this page
  // or a later editor open — goes to the network. Needed after a reset-results
  // new-session swap (AHA-41694): the backend regenerates slide ids, but this 6s
  // cache (also warmed on list hover) would otherwise serve the pre-swap slides.
  // Key must match the one built in getPresentationDetailsWithCacheAction.
  purgePresentationDetailsCacheAction: (_, presentationId) => {
    cache.purge(`fetchPresentationDetailsFromAPIAction-${presentationId}`)
  },

  preWarmCachePresentationDetailsAction: ({ dispatch }, { presentationId, payloadDataPresentation }) => {
    warmCachePresentationDetails(dispatch, { presentationId, payloadDataPresentation })
  },

  preWarmCachePresentationAttributesAction: ({ dispatch }, { presentationId }) => {
    warmCachePresentationAttributes(dispatch, { presentationId })
  },
}
const getters = {
  maxPresentationCacheTimeInSeconds: () => maxPresentationCacheTimeInSeconds,
}

export default {
  namespaced: true,
  actions,
  getters,
}
